import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import moment from "moment";
import { resolveBillingNames } from "../../../utils";

// Define Styles
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    padding: 30,
    backgroundColor: "#ffffff",
    color: "#333",
  },
  // Header Section
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    borderBottomWidth: 1,
    borderColor: "#e0e0e0",
    paddingBottom: 10,
  },
  logoSection: {
    width: "30%",
  },
  logo: {
    width: 80,
    height: 60,
    objectFit: "contain",
  },
  companyDetails: {
    width: "70%",
    textAlign: "right",
  },
  companyName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1a237e",
    textTransform: "uppercase",
  },
  companyText: {
    fontSize: 9,
    color: "#555",
    marginBottom: 2,
  },

  // Title Bar
  titleBar: {
    backgroundColor: "#f5f5f5",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 8,
    marginBottom: 15,
    borderRadius: 4,
    borderLeftWidth: 4,
    borderLeftColor: "#1a237e",
  },
  titleText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1a237e",
    textTransform: "uppercase",
  },
  receiptInfo: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#333",
  },

  // Info Grid
  gridContainer: {
    flexDirection: "row",
    marginBottom: 20,
    gap: 20,
  },
  colLeft: {
    width: "50%",
    paddingRight: 10,
  },
  colRight: {
    width: "50%",
    paddingLeft: 10,
    borderLeftWidth: 1,
    borderLeftColor: "#eee",
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#888",
    marginBottom: 5,
    textTransform: "uppercase",
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  infoLabel: {
    width: "35%",
    fontSize: 9,
    color: "#666",
  },
  infoValue: {
    width: "65%",
    fontSize: 9,
    color: "#000",
  },

  // Payment Box
  paymentBox: {
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 4,
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#1a237e",
    padding: 6,
  },
  th: {
    color: "#fff",
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "left",
  },
  tableRow: {
    flexDirection: "row",
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  td: {
    fontSize: 10,
    color: "#333",
  },

  // Total Section
  totalSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 10,
  },
  totalBox: {
    width: "50%",
    backgroundColor: "#e8f5e9",
    padding: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#c8e6c9",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#2e7d32",
  },
  totalValue: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2e7d32",
  },

  // Footer
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    marginTop: 30,
  },
  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  signatureBox: {
    borderTopWidth: 1,
    borderColor: "#999",
    width: "30%",
    textAlign: "center",
    paddingTop: 5,
    fontSize: 8,
    color: "#666",
  },
  disclaimer: {
    textAlign: "center",
    fontSize: 7,
    color: "#999",
    borderTopWidth: 1,
    borderColor: "#eee",
    paddingTop: 8,
  },
});

const ReceiptDocument = ({ receipt, bookingDetails, hotelProfile }) => {
  // --- SAFE DATA PARSING (CRASH PREVENTION) ---

  const hotel = {
    name: hotelProfile?.property?.name || hotelProfile?.name || "Hotel",
    address: hotelProfile?.property?.address || hotelProfile?.address || "",
    email:
      hotelProfile?.property?.reservation_email || hotelProfile?.email || "",
    phone:
      hotelProfile?.property?.reservation_mobile || hotelProfile?.mobile || "",
    gst: hotelProfile?.gstin || "",
    logo: hotelProfile?.logo || "", // Must be empty string if null, not null
  };
  const { primary: hotelPrimaryName, secondary: hotelSecondaryName } =
    resolveBillingNames(hotelProfile?.trade_name, hotel.name, hotelProfile?.gst_enabled);

  const guest = {
    name:
      [bookingDetails?.title, bookingDetails?.name].filter(Boolean).join(" ") ||
      "Guest",
    mobile: bookingDetails?.guest_mobile || "",
    address: bookingDetails?.guest_address || "",
  };

  const booking = {
    id: bookingDetails?.booking_id || "",
    checkIn: bookingDetails?.checkin_date
      ? moment(bookingDetails.checkin_date * 1000).format("DD MMM YYYY")
      : "",
    checkOut: bookingDetails?.checkout_date
      ? moment(bookingDetails.checkout_date * 1000).format("DD MMM YYYY")
      : "",
    roomInfo: bookingDetails?.rooms?.[0]?.room_id?.room_no
      ? `${bookingDetails.rooms[0].room_id.room_no} (${
          bookingDetails.rooms[0]?.room_id?.roomtype_id?.name || ""
        })`
      : "",
  };
  console.log("💥");
  const payment = {
    receipt_no: receipt?.receipt_no || "-",
    date: receipt?.date
      ? moment(Number(receipt.date)).format("DD MMM YYYY")
      : "",
    mode: receipt?.mode || "-",
    amount: Number(receipt?.amount || 0).toFixed(2),
    transaction_ref: receipt?.transaction_ref || "",
    bank_name: receipt?.bank_name || "",
    instrument_date: receipt?.instrument_date
      ? moment(Number(receipt.instrument_date)).format("DD MMM YYYY")
      : "",
    remarks: receipt?.remarks || "",
    collected_by: receipt?.collected_by || "Authorized Signatory",
  };
  return (
    <Document>
      <Page size="A5" orientation="landscape" style={styles.page}>
        {/* --- HEADER --- */}
        <View style={styles.headerContainer}>
          <View style={styles.logoSection}>
            {/* CHECK: Only render Image if source exists */}
            {hotel.logo ? <Image style={styles.logo} src={hotel.logo} /> : null}
          </View>
          <View style={styles.companyDetails}>
            <Text style={styles.companyName}>{hotelPrimaryName}</Text>
            {hotelSecondaryName ? (
              <Text style={styles.companyText}>{hotelSecondaryName}</Text>
            ) : null}
            {hotel.address ? (
              <Text style={styles.companyText}>{hotel.address}</Text>
            ) : null}

            <Text style={styles.companyText}>
              {/* Use || "" to prevent crash if one is missing */}
              {hotel.email ? `Email: ${hotel.email}` : ""}
              {hotel.email && hotel.phone ? " | " : ""}
              {hotel.phone ? `Phone: ${hotel.phone}` : ""}
            </Text>

            {hotel.gst ? (
              <Text style={styles.companyText}>GSTIN: {hotel.gst}</Text>
            ) : null}
          </View>
        </View>

        {/* --- TITLE BAR --- */}
        <View style={styles.titleBar}>
          <Text style={styles.titleText}>Payment Receipt</Text>
          <View style={{ alignItems: "flex-end" }}>
            <Text style={styles.receiptInfo}>#{payment.receipt_no}</Text>
            <Text style={{ fontSize: 9, color: "#666" }}>
              Date: {payment.date}
            </Text>
          </View>
        </View>

        {/* --- DETAILS GRID --- */}
        <View style={styles.gridContainer}>
          {/* Guest Details */}
          <View style={styles.colLeft}>
            <Text style={styles.sectionTitle}>Received From:</Text>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Name:</Text>
              <Text style={styles.infoValue}>{guest.name}</Text>
            </View>

            {guest.mobile ? (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Mobile:</Text>
                <Text style={styles.infoValue}>{guest.mobile}</Text>
              </View>
            ) : null}

            {guest.address ? (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Address:</Text>
                <Text style={styles.infoValue}>
                  {guest.address.length > 40
                    ? guest.address.substring(0, 40) + "..."
                    : guest.address}
                </Text>
              </View>
            ) : null}
          </View>

          {/* Booking Details */}
          <View style={styles.colRight}>
            <Text style={styles.sectionTitle}>Booking Reference:</Text>

            {booking.id ? (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Booking ID:</Text>
                <Text style={styles.infoValue}>{booking.id}</Text>
              </View>
            ) : null}

            {booking.roomInfo ? (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Room No:</Text>
                <Text style={styles.infoValue}>{booking.roomInfo}</Text>
              </View>
            ) : null}

            {booking.checkIn && booking.checkOut ? (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Stay Dates:</Text>
                <Text style={styles.infoValue}>
                  {booking.checkIn} to {booking.checkOut}
                </Text>
              </View>
            ) : null}
          </View>
        </View>

        {/* --- PAYMENT TABLE --- */}
        <View style={styles.paymentBox}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.th, { width: "30%" }]}>Payment Mode</Text>
            <Text style={[styles.th, { width: "40%" }]}>Details</Text>
            <Text style={[styles.th, { width: "30%", textAlign: "right" }]}>
              Amount
            </Text>
          </View>

          {/* Body */}
          <View style={styles.tableRow}>
            <View style={{ width: "30%" }}>
              <Text
                style={[
                  styles.td,
                  { textTransform: "uppercase", fontWeight: "bold" },
                ]}
              >
                {payment.mode}
              </Text>
            </View>

            <View style={{ width: "40%" }}>
              {payment.transaction_ref ? (
                <Text style={styles.td}>Ref: {payment.transaction_ref}</Text>
              ) : (
                // <Text
                //   style={[
                //     styles.td,
                //     { color: "#999", fontStyle: "italic", fontSize: 8 },
                //   ]}
                // >
                //   N/A
                // </Text> 
                null
              )}

              {payment.bank_name ? (
                <Text style={{ fontSize: 8, color: "#666", marginTop: 2 }}>
                  Bank: {payment.bank_name}
                </Text>
              ) : null}

              {payment.instrument_date ? (
                <Text style={{ fontSize: 8, color: "#666" }}>
                  Date: {payment.instrument_date}
                </Text>
              ) : null}

              {payment.collected_by ? (
                <Text style={{ fontSize: 8, color: "#666" }}>
                  Collected by: {payment.collected_by}
                </Text>
              ) : null}
            </View>

            <View style={{ width: "30%" }}>
              <Text
                style={[styles.td, { textAlign: "right", fontWeight: "bold" }]}
              >
                {/* Hardcode currency symbol to avoid font issues with INR symbol */}
                Rs. {payment.amount}
              </Text>
            </View>
          </View>
        </View>

        {/* --- TOTAL & REMARKS --- */}
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <View style={{ width: "45%" }}>
            {payment.remarks ? (
              <>
                <Text style={styles.sectionTitle}>Remarks:</Text>
                <Text
                  style={{ fontSize: 9, fontStyle: "italic", color: "#555" }}
                >
                  {payment.remarks}
                </Text>
              </>
            ) : null}
          </View>

          <View style={styles.totalBox}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>TOTAL RECEIVED</Text>
              <Text style={styles.totalValue}>Rs. {payment.amount}</Text>
            </View>
          </View>
        </View>

        {/* --- FOOTER --- */}
        <View style={styles.footer}>
          {/* <View style={styles.signatureRow}>
            <Text style={styles.signatureBox}>
                Guest Signature
            </Text>
            <Text style={styles.signatureBox}>
                {payment.collected_by}
                {"\n"}(Reception/Accounts)
            </Text>
          </View> */}
          <Text style={styles.disclaimer}>
            Thank you for your payment. This is a computer-generated receipt and
            does not require a physical signature.
            {hotel.gst ? ` | GSTIN: ${hotel.gst}` : ""}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default ReceiptDocument;
